import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../../services/supabaseClient';
import { describeError } from '../../services/errorMessage';
import { fetchEinweisungen, fetchOnlineTeilnahmen } from '../../services/sigeEinweisungService';
import {
  SigeEinweisung, SigeProject, Arbeitsschritt, Gefaehrdung, Schutzmassnahme, SigeBlock, EinweisungOnlineTeilnahme,
} from '../sige-plan/types';
import { gruppiereOnlineJeEinweisung, sortiereEinweisungen } from './einweisungLogic';

/**
 * Liest die Online-Teilnahmen des Projekts — FAIL-OPEN.
 *
 * Fehlt die Tabelle noch (Migration Nr. 97 nicht gelaufen) oder klemmt die
 * Abfrage, bleibt die Ablage trotzdem bedienbar: dann gibt es eben keine
 * Online-Zeilen. Der Link-Knopf meldet einen fehlenden Unterbau im Klartext,
 * das Laden der Ablage darf daran nie scheitern.
 */
const ladeOnlineSicher = async (projectId: string): Promise<EinweisungOnlineTeilnahme[]> => {
  try {
    return await fetchOnlineTeilnahmen(projectId);
  } catch (e) {
    console.warn('[useEinweisungen] Online-Teilnahmen nicht lesbar — Ablage laeuft ohne sie weiter', e);
    return [];
  }
};

/**
 * Das Laden der Einweisungs-Ablage — der einzige Zustand, den der Container
 * selbst haelt.
 *
 * Bewusst ein Hook und keine Logik in der Komponente: die Entscheidungen
 * (Sortierung, Einfuegen/Ersetzen, Gruppieren) stehen als pure Funktionen
 * daneben in einweisungLogic.ts und sind ohne DOM pruefbar; hier bleibt nur
 * das Nachladen.
 *
 * Die Online-Teilnahmen (eigene Tabelle, Plan §1.1) kommen mit EINER Abfrage
 * je Projekt und werden je Einweisung verteilt (`onlineJeEinweisung`). Kein
 * Realtime: Knopf „Aktualisieren" + Neuladen beim Oeffnen reichen.
 */
export const useEinweisungen = (projectId: string | null | undefined) => {
  const [einweisungen, setEinweisungen] = useState<SigeEinweisung[]>([]);
  const [online, setOnline] = useState<EinweisungOnlineTeilnahme[]>([]);
  const [laden, setLaden] = useState(true);
  const [onlineLaden, setOnlineLaden] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const neuLaden = useCallback(async () => {
    if (!projectId) {
      setEinweisungen([]);
      setOnline([]);
      setLaden(false);
      return;
    }
    setLaden(true);
    setFehler(null);
    try {
      const [liste, onlineListe] = await Promise.all([
        fetchEinweisungen(projectId),
        ladeOnlineSicher(projectId),
      ]);
      setEinweisungen(sortiereEinweisungen(liste));
      setOnline(onlineListe);
    } catch (e) {
      setFehler(describeError(e));
    } finally {
      setLaden(false);
    }
  }, [projectId]);

  useEffect(() => { void neuLaden(); }, [neuLaden]);

  /**
   * Nur die Online-Teilnahmen nachladen — fuer „Aktualisieren" in der
   * Teilnahmen-Ansicht. Die Ablage selbst bleibt stehen (kein Lade-Kreisel
   * ueber der ganzen Liste, kein Verlust der Auswahl).
   */
  const onlineNeuLaden = useCallback(async () => {
    if (!projectId) return;
    setOnlineLaden(true);
    try {
      setOnline(await ladeOnlineSicher(projectId));
    } finally {
      setOnlineLaden(false);
    }
  }, [projectId]);

  const onlineJeEinweisung = useMemo(() => gruppiereOnlineJeEinweisung(online), [online]);

  /**
   * Nimmt eine geaenderte Einweisung in die Liste auf — vorhandene wird
   * ersetzt, neue eingereiht.
   *
   * WARUM nicht einfach neu laden: nach jedem Tastendruck im Editor eine
   * Abfrage zu fahren waere auf der Baustelle (schmale Leitung) spuerbar, und
   * die Antwort des Servers steht ohnehin schon im Ergebnis des Schreibens.
   */
  const uebernehmen = useCallback((geaendert: SigeEinweisung) => {
    setEinweisungen((prev) => {
      const ohne = prev.filter((e) => e.id !== geaendert.id);
      return sortiereEinweisungen([...ohne, geaendert]);
    });
  }, []);

  const entfernen = useCallback((id: string) => {
    setEinweisungen((prev) => prev.filter((e) => e.id !== id));
  }, []);

  return {
    einweisungen, online, onlineJeEinweisung, laden, onlineLaden, fehler,
    neuLaden, onlineNeuLaden, uebernehmen, entfernen,
  };
};

/**
 * Der Plan, aus dem der Vorschlag gebaut wird — flach, so wie
 * buildEinweisungVorschlag ihn erwartet.
 *
 * `sigeProject` darf null sein: gibt es fuer das Projekt noch keinen Plan,
 * liefert der Vorschlag trotzdem die fachlichen Standardtexte (Aufbau,
 * Notfall, Pflichten). Ein fehlender Plan ist also kein Grund, den Knopf zu
 * sperren.
 */
export interface PlanQuelle {
  sigeProject: SigeProject | null;
  arbeitsschritte: Arbeitsschritt[];
  gefaehrdungen: Gefaehrdung[];
  massnahmen: Schutzmassnahme[];
  bloecke: SigeBlock[];
}

const LEERE_QUELLE: PlanQuelle = {
  sigeProject: null,
  arbeitsschritte: [],
  gefaehrdungen: [],
  massnahmen: [],
  bloecke: [],
};

/**
 * Liest die NEUESTE, nicht geloeschte Planversion des Projekts samt Zeilenbaum.
 *
 * Erst auf Knopfdruck („Vorschlag aus dem Plan"), nicht beim Oeffnen der
 * Ablage: das sind fuenf Abfragen, die niemanden interessieren, der nur das
 * Blatt der letzten Woche ausdrucken will.
 *
 * Einzelne fehlgeschlagene Zweige werden uebersprungen statt geworfen — ein
 * Vorschlag ohne Gefaehrdungen ist besser als gar keiner, und die
 * Standardtexte stehen ohnehin.
 */
export const ladePlanQuelle = async (projectId: string): Promise<PlanQuelle> => {
  if (!projectId) return LEERE_QUELLE;

  const { data: planListe, error: planFehler } = await supabase
    .from('sige_projekte')
    .select('*')
    .eq('project_id', projectId)
    .is('deleted_at', null)
    .order('created_at', { ascending: false })
    .limit(1);
  if (planFehler) throw new Error(describeError(planFehler));

  const plan = ((planListe || [])[0] as SigeProject | undefined) ?? null;
  if (!plan) return LEERE_QUELLE;

  const { data: schritte } = await supabase
    .from('sige_arbeitsschritte')
    .select('*')
    .eq('sige_projekt_id', plan.id)
    .order('sortierung');
  const arbeitsschritte = (schritte || []) as Arbeitsschritt[];

  let gefaehrdungen: Gefaehrdung[] = [];
  if (arbeitsschritte.length > 0) {
    const { data } = await supabase
      .from('sige_gefaehrdungen')
      .select('*')
      .in('arbeitsschritt_id', arbeitsschritte.map((s) => s.id));
    gefaehrdungen = (data || []) as Gefaehrdung[];
  }

  let massnahmen: Schutzmassnahme[] = [];
  if (gefaehrdungen.length > 0) {
    const { data } = await supabase
      .from('sige_schutzmassnahmen')
      .select('*')
      .in('gefaehrdung_id', gefaehrdungen.map((g) => g.id));
    massnahmen = (data || []) as Schutzmassnahme[];
  }

  const { data: bloecke } = await supabase
    .from('sige_bloecke')
    .select('*')
    .eq('sige_projekt_id', plan.id);

  return {
    sigeProject: plan,
    arbeitsschritte,
    gefaehrdungen,
    massnahmen,
    bloecke: (bloecke || []) as SigeBlock[],
  };
};
